/**
 * Theme Cache Service
 * Handles caching of organization branding data with TTL support
 */

import {
  setItem,
  getItem,
  removeItem,
  clearByPrefix,
  hasItem,
  getExpiry,
  isExpired
} from '../utils/storage-utils';

// Default cache duration: 1 hour
const DEFAULT_CACHE_DURATION = 60 * 60 * 1000;

// Cache key prefix
const CACHE_PREFIX = 'neurograph:theme:';

/**
 * ThemeCache class for managing theme caching
 */
class ThemeCache {
  constructor(config = {}) {
    this.config = {
      cacheDuration: DEFAULT_CACHE_DURATION,
      storageType: 'localStorage', // Only localStorage for now
      ...config
    };
  }

  /**
   * Generate cache key for a domain
   *
   * @param {string} domain - The organization domain/slug
   * @returns {string} The cache key
   */
  getCacheKey(domain) {
    return `${CACHE_PREFIX}domain:${domain}`;
  }

  /**
   * Set theme in cache
   *
   * @param {string} domain - The organization domain/slug
   * @param {object} branding - The branding data
   * @param {object} tokens - The style tokens (optional)
   */
  set(domain, branding, tokens = null) {
    const key = this.getCacheKey(domain);
    const cacheData = {
      branding,
      tokens,
      domain,
      organizationId: branding.org_guid || null,
      cachedAt: new Date().toISOString(),
      version: '1.0'
    };

    setItem(key, cacheData, this.config.cacheDuration);
  }

  /**
   * Get theme from cache
   *
   * @param {string} domain - The organization domain/slug
   * @returns {object|null} The cached theme data or null
   */
  get(domain) {
    const key = this.getCacheKey(domain);
    return getItem(key);
  }

  /**
   * Check if theme exists in cache and is not expired
   *
   * @param {string} domain - The organization domain/slug
   * @returns {boolean} True if cached and valid
   */
  has(domain) {
    const key = this.getCacheKey(domain);
    return hasItem(key);
  }

  /**
   * Delete theme from cache
   *
   * @param {string} domain - The organization domain/slug
   */
  delete(domain) {
    const key = this.getCacheKey(domain);
    removeItem(key);
  }

  /**
   * Clear all cached themes
   */
  clear() {
    clearByPrefix(CACHE_PREFIX);
  }

  /**
   * Get expiry timestamp for a cached theme
   *
   * @param {string} domain - The organization domain/slug
   * @returns {number|null} The expiry timestamp or null
   */
  getExpiry(domain) {
    const key = this.getCacheKey(domain);
    return getExpiry(key);
  }

  /**
   * Check if a cached theme is expired
   *
   * @param {string} domain - The organization domain/slug
   * @returns {boolean} True if expired
   */
  isExpired(domain) {
    const key = this.getCacheKey(domain);
    return isExpired(key);
  }

  /**
   * Get cache info and statistics
   *
   * @returns {object} Cache statistics
   */
  getCacheInfo() {
    if (typeof window === 'undefined' || !window.localStorage) {
      return {
        enabled: false,
        count: 0,
        size: 0
      };
    }

    try {
      const keys = Object.keys(window.localStorage);
      const themeKeys = keys.filter(key => key.startsWith(CACHE_PREFIX));

      let totalSize = 0;
      themeKeys.forEach(key => {
        const value = window.localStorage.getItem(key);
        totalSize += value ? value.length : 0;
      });

      return {
        enabled: true,
        count: themeKeys.length,
        size: totalSize,
        cacheDuration: this.config.cacheDuration
      };
    } catch (error) {
      console.error('Error getting cache info:', error);
      return {
        enabled: false,
        count: 0,
        size: 0
      };
    }
  }

  /**
   * Set multiple themes in cache
   *
   * @param {Array} items - Array of {domain, branding, tokens} objects
   */
  setMany(items) {
    items.forEach(item => {
      this.set(item.domain, item.branding, item.tokens);
    });
  }

  /**
   * Get multiple themes from cache
   *
   * @param {Array<string>} domains - Array of domain slugs
   * @returns {Array} Array of cached theme data or null for each domain
   */
  getMany(domains) {
    return domains.map(domain => this.get(domain));
  }
}

// Export singleton instance
export default new ThemeCache();

// Export class for custom instances
export { ThemeCache };
